import _ from "lodash";

export const isFilterableTreeNode = (node, searchValue, skipTopicLevel = []) => {
  const regex = new RegExp(`(${searchValue})`, "gmi");
  return node && !skipTopicLevel.includes(node.topicLevel) && !regex.test(node.path || "");
};

export const filterTreeNode = (node, searchValue, skipTopicLevel = []) => {
  let result = undefined;

  if (node) {
    result = node;
    const children = node.children || [];

    if (children.length > 0) {
      result.children = children
        .map(el => filterTreeNode(el, searchValue, skipTopicLevel))
        .filter(el => el);
    } else if (isFilterableTreeNode(node, searchValue, skipTopicLevel)) {
      result = undefined;
    }

    if (
      result &&
      (node.key === "Component" || node.key === "Machine") &&
      (result.children || []).length === 0 &&
      isFilterableTreeNode(result, searchValue, skipTopicLevel)
    ) {
      result = undefined;
    }
  }

  return result;
};

/**
 * Utility function to evaluate a node whether match with test parameter.
 * @param {*} node Node to evaluate
 * @param {string|RegExp|Array|function} test Parameter to search
 *   * type string: test match string equality
 *   * type Array: test sort array and match with node if value is also Array
 *   * type RegExp: test is used as RegExp expression
 *   * type Object: test as like { keyToMach: withValue, keyToMatch2: withValue2, etc...}.
 *                  Field option is ignored because the test match with multiple keys.
 *   * type function: function is called with node parameter. Param2 is ignored.
 * @param {*} param2 Parameter with additional options.
 *   * field: field used to test node. default is "id"
 *   * originalNode: use original node (xml node) if value is true
 * @returns true when node match test
 */

export const testNode = (test, node, { field = "id", originalNode = false }) => {
  let result = false;
  const nodeToTest = originalNode ? node.node : node;

  if (node) {
    if (_.isFunction(test)) {
      result = test(node);
    } else if (nodeToTest) {
      const testValue = nodeToTest && nodeToTest[field];
      const normalizedValue = Array.isArray(testValue) ? testValue : [(testValue || "").toString()];

      if (_.isRegExp(test)) {
        result = !!normalizedValue.find(el => {
          return new RegExp(test).test(el);
        });
      } else if (Array.isArray(test)) {
        result = normalizedValue.sort().toString() === test.sort().toString();
      } else if (_.isPlainObject(test)) {
        result = Object.entries(test).every(([key, value]) => {
          return nodeToTest[key] === value;
        });
      } else {
        result = (testValue || "").toString() === (test || "").toString();
      }
    }
  }

  return result;
};

/**
 * Utility function to search a node or multiple nodes that match with test parameter.
 * @param {string|RegExp|Array|function} test Parameter to search. See testNode to further details.
 * @param {*} node Start point for search node or nodes
 * @param {*} param2 Parameter with additional options.
 *   * field: field used to test node. default is "id"
 *   * originalNode: use original node (xml node) if value is true
 *   * all: returns all nodes match with test if value is true
 * @returns node or array of nodes
 */

export const findNode = (test, node, { field = "id", originalNode = false, all = false } = {}) => {
  let result = all ? [] : undefined;
  const children = (node && node.children) || [];

  if (node) {
    const testValue = testNode(test, node, { field, originalNode });

    if (testValue) {
      if (all) {
        result.push(node);
      } else {
        result = node;
      }
    }
    if (children.length > 0) {
      if (all) {
        result = [
          ...result,
          ...children
            .map(el => findNode(test, el, { field, originalNode, all }))
            .reduce((el, acc) => [...acc, ...el], [])
        ];
      } else if (!result) {
        for (const child of children) {
          result = findNode(test, child, { field, originalNode, all });
          if (result) {
            break;
          }
        }
      }
    }
  }

  return result;
};

export const isCmsMachine = machine => {
  return getManufacturer(machine).includes("cms");
};

// Text in the format: "CMS Industries", "CMS",...
export const getManufacturer = machine => {
  return ((machine && machine.Manufacturer) || "").toLowerCase();
};

// For CMS: CDL, TFO, AWJ
export const getFamily = machine => {
  return ((machine && machine.MachineFamily) || "").toLowerCase();
};

export const cmsVariant = ["cms", "cms-spindle", "cms-wj", "cms-awj"];
export const isCmsVariant = variant => cmsVariant.includes(variant);
